import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X, ExternalLink, Sparkles, Globe, Newspaper, RefreshCw, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { logQuietError } from "@/lib/quiet-error";
import { getItem as ssGet, setItem as ssSet } from "@/lib/safe-storage";

/**
 * TeeBee Animated Splash Screen — Phase 10d-B (Part B).
 *
 * Behaviour:
 *  • Shown ONCE PER DAY for authenticated users when they land on /dashboard or /.
 *  • Anonymous landing visits get the splash on FIRST visit (per-browser).
 *  • Dismissal persists via localStorage (key: teebee_splash_shown_YYYY-MM-DD).
 *  • Skip button + Continue button + auto-dismiss on Escape.
 *  • News (3 local + 3 global) fetched in parallel from /api/news/education/bundle.
 *  • Reduced-motion aware — drops the bee flight animation if user prefers.
 *  • NEVER blocks app entry. 4s safety timeout closes splash if news + assets stall.
 *
 *  Hidden on: /login, /signup, /legal/*, /accept-invitation, public lesson/profile/resume links
 *             and any route the user opened from an external share URL.
 */

const TODAY = () => new Date().toISOString().slice(0, 10);
const STORAGE_PREFIX = "teebee_splash_shown_";
const TOGGLE_KEY = "teebee_splash_enabled";

const HIDE_ROUTE_PATTERNS = [
  /^\/login/,
  /^\/signup/,
  /^\/legal/,
  /^\/accept-invitation/,
  /^\/l\//,
  /^\/u\//,
  /^\/r\//,
  /^\/auth\//,
];

const TABS = [
  { key: "local",      label: "Local",      icon: Newspaper },
  { key: "global",     label: "Global",     icon: Globe },
  { key: "innovation", label: "Innovation", icon: Sparkles },
];

function shouldShowToday() {
  // Master enable switch — if user explicitly disables daily splash, never show.
  // NOTE: stored values are NON-SENSITIVE UI dismissal flags only ("1" / "off").
  if (ssGet(TOGGLE_KEY) === "off") return false;
  return ssGet(STORAGE_PREFIX + TODAY()) !== "1";
}

function markSeen() {
  // Non-sensitive: a single-character "1" flag scoped to a date string.
  ssSet(STORAGE_PREFIX + TODAY(), "1");
}

export default function SplashScreen() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [news, setNews] = useState({ local: [], global: [], innovation: [] });
  const [newsLoading, setNewsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("local");
  const [country] = useState("IN");                // future-proof for locale switch
  const closedRef = useRef(false);

  const prefersReducedMotion = useMemo(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false
  , []);

  // Decide whether to surface the splash.
  useEffect(() => {
    if (loading) return;
    if (closedRef.current) return;
    const path = location.pathname || "/";
    const blocked = HIDE_ROUTE_PATTERNS.some((r) => r.test(path));
    if (blocked) return;

    // Splash conditions:
    //   - Landing ('/') for anonymous OR authenticated on first daily visit
    //   - /dashboard for authenticated users on first daily visit
    const isLanding = path === "/";
    const isDashboard = path === "/dashboard";
    if (!(isLanding || isDashboard)) return;

    if (!shouldShowToday()) return;
    setVisible(true);
  }, [loading, location.pathname]);

  // Fetch news once when splash opens.
  useEffect(() => {
    if (!visible) return;
    let alive = true;
    setNewsLoading(true);
    api.get(`/news/education/bundle?country=${country}`, { timeout: 4000 })
      .then(({ data }) => { if (alive) setNews({ local: data.local || [], global: data.global || [], innovation: data.innovation || [] }); })
      .catch((e) => { logQuietError("splash.news", e); })
      .finally(() => { if (alive) setNewsLoading(false); });
    return () => { alive = false; };
  }, [visible, country]);

  // ESC closes.
  useEffect(() => {
    if (!visible) return;
    const handler = (e) => { if (e.key === "Escape") close(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Lock body scroll when open.
  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [visible]);

  const close = (goDashboard = false) => {
    closedRef.current = true;
    markSeen();
    setVisible(false);
    if (goDashboard && user) navigate("/dashboard");
  };

  if (!visible) return null;

  const items = news[activeTab] || [];
  const greeting = user?.name ? `Good to see you, ${user.name.split(" ")[0]}` : "Welcome to TeeBee";

  return (
    <div
      className="fixed inset-0 z-[100] tb-splash-bg flex items-center justify-center px-4 py-6 sm:py-10"
      role="dialog"
      aria-modal="true"
      aria-label="TeeBee daily welcome"
      data-testid="splash-screen"
    >
      {/* Dim/blur backdrop */}
      <div className="absolute inset-0 bg-gradient-to-br from-violet-950/95 via-indigo-950/95 to-slate-950/95" />
      {/* Soft radial accents */}
      <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-amber-400/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-violet-400/20 blur-3xl pointer-events-none" />

      {/* Bee flight animation (CSS) */}
      {!prefersReducedMotion && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
          <span className="tb-bee-trail">
            <BeeMascot className="h-8 w-8 sm:h-10 sm:w-10 drop-shadow-[0_4px_10px_rgba(255,200,0,0.4)]" />
          </span>
          {/* Dotted trail */}
          <span className="tb-bee-dots" />
        </div>
      )}

      {/* Close (top-right) */}
      <button
        onClick={() => close(false)}
        className="absolute top-4 right-4 sm:top-6 sm:right-6 z-20 inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur transition"
        aria-label="Skip splash"
        data-testid="splash-close"
      >
        <X className="h-4 w-4" />
      </button>

      {/* Content card */}
      <div
        className="relative z-10 w-full max-w-6xl grid lg:grid-cols-12 gap-6 lg:gap-10 max-h-[92vh] overflow-hidden"
      >
        {/* LEFT — Brand */}
        <div className="lg:col-span-5 flex flex-col justify-center items-center text-center text-white px-2 lg:px-4 tb-splash-fade">
          <div className="relative inline-block">
            {/* Soft amber + violet halo so the bee icon really lifts off the dark backdrop */}
            <span className="absolute inset-0 rounded-full bg-amber-400/30 blur-3xl pointer-events-none" />
            <span className="absolute inset-0 rounded-full bg-violet-400/20 blur-2xl pointer-events-none" />
            {/* Clean icon-only artwork (TB + BuddyBee + globe) — no baked-in tagline that gets lost in dark mode */}
            <img
              src="/icon-512.png?v=11"
              alt="TeeBee"
              className="relative h-28 w-28 sm:h-32 sm:w-32 lg:h-36 lg:w-36 object-contain tb-logo-art"
              draggable={false}
            />
          </div>

          {/* HTML-rendered wordmark — perfect dark-mode contrast, no dim PNG text */}
          <h1
            className="mt-5 font-display text-5xl sm:text-6xl tracking-tight font-medium leading-none"
            data-testid="splash-headline"
          >
            <span className="text-white">Tee</span>
            <span className="bg-gradient-to-r from-violet-300 to-fuchsia-300 bg-clip-text text-transparent">Bee</span>
            <span
              aria-hidden
              className="tb-logo-tm tb-logo-tm-sm relative inline-block align-super -top-3"
              style={{ marginLeft: "2px" }}
            >™</span>
          </h1>
          <p className="mt-1 text-[11px] uppercase tracking-[0.32em] text-white/50 font-medium">
            by Factskoot
          </p>
          <p className="mt-3 text-sm sm:text-base text-white/85 font-medium max-w-md">
            The AI Global OS and Marketplace for Educators
          </p>
          <p className="mt-2 text-xs text-white/55 max-w-md">
            Create lessons. Build your profile. Get discovered globally.
          </p>

          <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-white/90 text-xs backdrop-blur">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-300 animate-pulse" />
            {greeting}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 w-full max-w-sm">
            {user ? (
              <Button
                onClick={() => close(true)}
                className="flex-1 rounded-full h-11 bg-amber-400 hover:bg-amber-300 text-slate-900 font-medium"
                data-testid="splash-continue"
              >
                Continue to TeeBee
              </Button>
            ) : (
              <Link to="/signup" className="flex-1" onClick={() => close(false)}>
                <Button className="w-full rounded-full h-11 bg-amber-400 hover:bg-amber-300 text-slate-900 font-medium" data-testid="splash-signup">
                  Get started free
                </Button>
              </Link>
            )}
            <Button
              variant="ghost"
              onClick={() => close(false)}
              className="rounded-full h-11 text-white/80 hover:text-white hover:bg-white/10"
              data-testid="splash-skip"
            >
              Skip for today
            </Button>
          </div>

          <p className="mt-4 text-[10px] text-white/40 italic">
            BuddyBee: <span className="text-white/60 not-italic">{buddyMicrocopy()}</span>
          </p>
        </div>

        {/* RIGHT — News */}
        <div className="lg:col-span-7 flex flex-col bg-white/[0.06] border border-white/10 backdrop-blur-xl rounded-3xl p-4 sm:p-6 text-white max-h-[88vh] overflow-hidden tb-splash-fade-delayed">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-amber-300">Today in Education</p>
              <h2 className="font-display text-xl sm:text-2xl tracking-tight mt-0.5">Fresh local + global updates for educators</h2>
            </div>
            {newsLoading && (
              <RefreshCw className="h-4 w-4 animate-spin text-white/60" aria-hidden />
            )}
          </div>

          {/* Tab strip */}
          <div className="mt-4 flex gap-1 bg-white/5 rounded-full p-1 self-start" data-testid="splash-tabs">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition ${
                  activeTab === key ? "bg-white/15 text-white" : "text-white/60 hover:text-white/90"
                }`}
                data-testid={`splash-tab-${key}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>

          {/* News list */}
          <div className="mt-4 flex-1 overflow-y-auto pr-1 tb-no-scrollbar" data-testid="splash-news-list">
            {newsLoading && items.length === 0 ? (
              <NewsSkeleton />
            ) : items.length === 0 ? (
              <EmptyNews />
            ) : (
              <ul className="space-y-2.5">
                {items.slice(0, 6).map((n, i) => (
                  <NewsCard key={n.link || i} card={n} />
                ))}
              </ul>
            )}
          </div>

          <p className="mt-3 text-[10px] text-white/40 leading-relaxed">
            News links open external websites. TeeBee does not control third-party content.
            Sources: Google News and partner publishers.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ---------------- helpers ---------------- */

function buddyMicrocopy() {
  const lines = [
    "Buzzing in with your daily education update.",
    "Ready to teach smarter today?",
    "Let's make today's teaching easier.",
    "Good morning, educator!",
  ];
  return lines[new Date().getDate() % lines.length];
}

function NewsCard({ card }) {
  return (
    <li>
      <a
        href={card.link}
        target="_blank"
        rel="noopener noreferrer"
        className="block rounded-xl px-3 py-2.5 bg-white/[0.04] hover:bg-white/[0.08] border border-white/5 hover:border-white/15 transition group"
        data-testid="splash-news-card"
      >
        <div className="flex items-start gap-3">
          {card.image_url ? (
            <img
              src={card.image_url}
              alt=""
              loading="lazy"
              className="h-12 w-12 sm:h-14 sm:w-14 rounded-lg object-cover bg-white/5 shrink-0"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ) : (
            <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-lg bg-gradient-to-br from-violet-500/40 to-amber-500/30 grid place-items-center shrink-0">
              <Newspaper className="h-5 w-5 text-white/70" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium leading-snug line-clamp-2 group-hover:text-amber-200 transition" data-testid="splash-news-title">
              {card.title}
            </p>
            <div className="mt-1 flex items-center gap-2 text-[11px] text-white/55">
              <span className="truncate max-w-[120px]">{card.source}</span>
              <span>•</span>
              <span className="truncate">{card.region}</span>
              <span className="ml-auto inline-flex items-center gap-1 text-white/40 group-hover:text-amber-200 transition">
                Read <ExternalLink className="h-3 w-3" />
              </span>
            </div>
          </div>
        </div>
      </a>
    </li>
  );
}

function NewsSkeleton() {
  return (
    <ul className="space-y-2.5" aria-hidden>
      {[1, 2, 3, 4].map((i) => (
        <li key={i} className="rounded-xl px-3 py-2.5 bg-white/[0.03] border border-white/5">
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-lg bg-white/10 animate-pulse" />
            <div className="flex-1 space-y-2">
              <div className="h-3 bg-white/10 rounded animate-pulse w-5/6" />
              <div className="h-3 bg-white/10 rounded animate-pulse w-2/3" />
              <div className="h-2 bg-white/5 rounded animate-pulse w-1/3" />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function EmptyNews() {
  return (
    <div className="text-center py-10" data-testid="splash-news-empty">
      <Newspaper className="h-8 w-8 mx-auto text-white/30" />
      <p className="mt-3 text-sm text-white/60">Daily education updates are loading soon.</p>
      <p className="text-xs text-white/40 mt-1">Hop in — your dashboard is ready.</p>
    </div>
  );
}

function BeeMascot({ className = "" }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden focusable="false">
      <defs>
        <radialGradient id="bee-body" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="#FFE082" />
          <stop offset="100%" stopColor="#FFB300" />
        </radialGradient>
      </defs>
      {/* Wings */}
      <ellipse cx="20" cy="20" rx="11" ry="8" fill="rgba(255,255,255,0.85)" />
      <ellipse cx="44" cy="20" rx="11" ry="8" fill="rgba(255,255,255,0.85)" />
      {/* Body */}
      <ellipse cx="32" cy="36" rx="16" ry="13" fill="url(#bee-body)" />
      <rect x="20" y="30" width="24" height="3.5" fill="#3b2410" rx="1.5" />
      <rect x="20" y="38" width="24" height="3.5" fill="#3b2410" rx="1.5" />
      {/* Eye */}
      <circle cx="42" cy="34" r="2.6" fill="#1a1a1a" />
      <circle cx="43" cy="33.2" r="0.9" fill="#fff" />
      {/* Smile */}
      <path d="M38 39 q3 3 6 0" stroke="#1a1a1a" strokeWidth="1.4" fill="none" strokeLinecap="round" />
      {/* Antennae */}
      <path d="M30 22 q-2 -6 -6 -7" stroke="#1a1a1a" strokeWidth="1.4" fill="none" strokeLinecap="round" />
      <path d="M34 22 q2 -6 6 -7" stroke="#1a1a1a" strokeWidth="1.4" fill="none" strokeLinecap="round" />
      <circle cx="24" cy="15" r="1.4" fill="#1a1a1a" />
      <circle cx="40" cy="15" r="1.4" fill="#1a1a1a" />
    </svg>
  );
}
